import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import jwt_decode from 'jwt-decode';

import { setAuthToken } from '../../utils/api';

// Types
interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  school?: string;
  grade?: string;
  interests?: string[];
  achievements?: string[];
}

interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  school?: string;
  grade?: string;
  interests?: string[];
  achievements?: string[];
}

interface AuthResponse {
  access_token: string;
  refresh_token: string;
  user: User;
}

// Helper function to check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const decoded: any = jwt_decode(token);
    return decoded.exp < Date.now() / 1000;
  } catch (error) {
    return true;
  }
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await axios.post<AuthResponse>('/api/auth/login', credentials);
      
      // Store tokens in local storage
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('refreshToken', response.data.refresh_token);
      
      // Set auth token in axios headers
      setAuthToken(response.data.access_token);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Login failed. Please check your credentials.'
      );
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData: RegisterData, { rejectWithValue }) => {
    try {
      const response = await axios.post<AuthResponse>('/api/auth/register', userData);
      
      // Store tokens in local storage
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('refreshToken', response.data.refresh_token);
      
      // Set auth token in axios headers
      setAuthToken(response.data.access_token);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Registration failed. Please try again.'
      );
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      // Call logout endpoint (optional, since JWT is stateless)
      await axios.post('/api/auth/logout');
      
      // Remove tokens from local storage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      
      // Remove auth token from axios headers
      setAuthToken(null);
      
      return null;
    } catch (error: any) {
      // Even if the API call fails, we still want to log out locally
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      setAuthToken(null);
      
      return rejectWithValue(
        error.response?.data?.error || 'Logout failed.'
      );
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      // Set the refresh token in the header for this request only
      const response = await axios.post<{ access_token: string }>('/api/auth/refresh', {}, {
        headers: {
          Authorization: `Bearer ${refreshToken}`
        }
      });
      
      // Store new access token
      localStorage.setItem('token', response.data.access_token);
      
      // Set auth token in axios headers
      setAuthToken(response.data.access_token);
      
      return response.data;
    } catch (error: any) {
      // If refresh fails, log out
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      setAuthToken(null);
      
      return rejectWithValue(
        error.response?.data?.error || 'Session expired. Please log in again.'
      );
    }
  }
);

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No token found');
      }
      
      // Check if token is expired
      if (isTokenExpired(token)) {
        // Try to refresh the token
        return await dispatch(refreshToken()).unwrap();
      }
      
      // Set auth token in axios headers
      setAuthToken(token);
      
      // Get current user data
      const response = await axios.get<{ user: User }>('/api/auth/me');
      
      return {
        access_token: token,
        user: response.data.user
      };
    } catch (error: any) {
      // If any error occurs, clear auth state
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      setAuthToken(null);
      
      return rejectWithValue(
        error.response?.data?.error || 'Authentication failed.'
      );
    }
  }
);

// Initial state
const initialState: AuthState = {
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  user: null,
  loading: true,
  error: null
};

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<AuthResponse>) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.access_token;
        state.user = action.payload.user;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action: PayloadAction<AuthResponse>) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.access_token;
        state.user = action.payload.user;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.token = null;
        state.isAuthenticated = false;
        state.user = null;
        state.loading = false;
      })
      
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.access_token;
        // Check if user property exists in the payload
        if ('user' in action.payload) {
          state.user = action.payload.user;
        }
      })
      .addCase(checkAuth.rejected, (state) => {
        state.token = null;
        state.isAuthenticated = false;
        state.user = null;
        state.loading = false;
      })
      
      // Refresh Token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.token = null;
        state.isAuthenticated = false;
        state.user = null;
      });
  }
});

export const { clearError } = authSlice.actions;

export default authSlice.reducer;
